import Dexie, { type Table } from 'dexie'
import type { DeliveryDraft, Pickup, PickupRecordDraft, WasteType } from '@/types'

type LegacyPickupRecordDraft = PickupRecordDraft & {
  recorded_at?: string
  started_at?: string
  photos?: Blob[]
  items?: Array<
    PickupRecordDraft['items'][number] & {
      notes?: string
      photos?: Blob[]
      segregation_status?: PickupRecordDraft['items'][number]['segregation_status']
    }
  >
}

export class PuroVientoDB extends Dexie {
  pickup_records_draft!: Table<PickupRecordDraft>
  delivery_drafts!: Table<DeliveryDraft>
  pickups!: Table<Pickup, number>
  waste_types!: Table<WasteType, number>

  constructor() {
    super('PuroVientoDB')

    this.version(1).stores({
      pickup_records_draft: '++id, pickup_id, pending_sync',
    })

    this.version(2)
      .stores({
        pickup_records_draft: '++id, pickup_id, pending_sync',
      })
      .upgrade(async (tx) => {
        await tx.table('pickup_records_draft').toCollection().modify((draft: LegacyPickupRecordDraft) => {
          const rootPhotos: Blob[] = draft.photos ?? []

          draft.items = (draft.items ?? []).map((item, idx) => ({
            ...item,
            segregation_status: item.segregation_status ?? '',
            notes: item.notes ?? '',
            photos: item.photos ?? (idx === 0 ? rootPhotos : []),
          }))

          delete draft.photos
        })
      })

    this.version(3)
      .stores({
        pickup_records_draft: '++id, pickup_id, pending_sync',
      })
      .upgrade(async (tx) => {
        await tx.table('pickup_records_draft').toCollection().modify((draft: LegacyPickupRecordDraft) => {
          if (!draft.started_at && draft.recorded_at) {
            draft.started_at = draft.recorded_at
          }

          delete draft.recorded_at
        })
      })

    this.version(4).stores({
      pickup_records_draft: '++id, pickup_id, pending_sync',
      delivery_drafts: '++id, pending_sync',
    })

    this.version(5).stores({
      pickup_records_draft: '++id, pickup_id, pending_sync',
      delivery_drafts: '++id, pending_sync',
      pickups: 'id, scheduled_date, status',
    })

    // v6: normaliza pending_sync de boolean a numérico 1/0 para que el índice
    // de Dexie funcione correctamente (IndexedDB no indexa valores booleanos).
    this.version(6)
      .stores({
        pickup_records_draft: '++id, pickup_id, pending_sync',
        delivery_drafts: '++id, pending_sync',
        pickups: 'id, scheduled_date, status',
      })
      .upgrade(async (tx) => {
        await tx.table('pickup_records_draft').toCollection().modify((draft: { pending_sync: boolean | 0 | 1 }) => {
          draft.pending_sync = draft.pending_sync ? 1 : 0
        })
        await tx.table('delivery_drafts').toCollection().modify((draft: { pending_sync: boolean | 0 | 1 }) => {
          draft.pending_sync = draft.pending_sync ? 1 : 0
        })
      })

    // v7: caché offline de tipos de residuo para disponibilidad sin conexión.
    this.version(7).stores({
      pickup_records_draft: '++id, pickup_id, pending_sync',
      delivery_drafts: '++id, pending_sync',
      pickups: 'id, scheduled_date, status',
      waste_types: 'id, waste_subcategory_id',
    })

    // v8: backfill de local_uuid en drafts preexistentes para que los borradores
    // ya encolados también sincronicen de forma idempotente al reconectar.
    this.version(8)
      .stores({
        pickup_records_draft: '++id, pickup_id, pending_sync',
        delivery_drafts: '++id, pending_sync',
        pickups: 'id, scheduled_date, status',
        waste_types: 'id, waste_subcategory_id',
      })
      .upgrade(async (tx) => {
        await tx.table('pickup_records_draft').toCollection().modify((draft: PickupRecordDraft) => {
          if (!draft.local_uuid) {
            draft.local_uuid = crypto.randomUUID()
          }
        })
      })
  }
}

export const db = new PuroVientoDB()
db.open().catch((err) => {
  console.error('Error al abrir PuroVientoDB:', err)
})

// ─── Helpers ─────────────────────────────────────────────────────────────────

export async function saveDraft(
  draft: Omit<PickupRecordDraft, 'id'>
): Promise<number> {
  return db.pickup_records_draft.add(draft)
}

export async function updateDraft(
  id: number,
  changes: Partial<PickupRecordDraft>
): Promise<void> {
  await db.pickup_records_draft.update(id, changes)
}

export async function getDraftByPickupId(
  pickupId: number
): Promise<PickupRecordDraft | undefined> {
  return db.pickup_records_draft
    .where('pickup_id')
    .equals(pickupId)
    .filter((d) => d.pending_sync === 1)
    .first()
}

export async function getPendingDrafts(): Promise<PickupRecordDraft[]> {
  return db.pickup_records_draft.where('pending_sync').equals(1).toArray()
}

export async function markDraftSynced(id: number, serverId: number): Promise<void> {
  await db.pickup_records_draft.update(id, {
    pending_sync: 0,
    server_id: serverId,
  })
}

export async function deleteDraft(id: number): Promise<void> {
  await db.pickup_records_draft.delete(id)
}

export async function getAllDrafts(): Promise<PickupRecordDraft[]> {
  return db.pickup_records_draft.toArray()
}

// ─── Helpers caché local de retiros ─────────────────────────────────────────

export async function saveCachedPickups(
  pickups: Pickup[],
  scope?: { dateFrom: string; dateTo: string; status?: string }
): Promise<void> {
  if (pickups.length === 0 && !scope) return

  await db.transaction('rw', db.pickups, async () => {
    // When fetching without a status filter we have a complete picture of the
    // date range, so remove pickups that the server no longer returns.
    if (scope && !scope.status) {
      const incomingIds = new Set(pickups.map((p) => p.id))
      const existingKeys = await db.pickups
        .where('scheduled_date')
        .between(scope.dateFrom, scope.dateTo, true, true)
        .primaryKeys()
      const staleIds = (existingKeys as number[]).filter((id) => !incomingIds.has(id))
      if (staleIds.length > 0) {
        await db.pickups.bulkDelete(staleIds)
      }
    }
    if (pickups.length > 0) {
      await db.pickups.bulkPut(pickups)
    }
  })
}

export async function getCachedPickup(id: number): Promise<Pickup | undefined> {
  return db.pickups.get(id)
}

export async function removeCachedPickup(id: number): Promise<void> {
  await db.pickups.delete(id)
}

export async function getCachedPickupIds(): Promise<number[]> {
  const keys = await db.pickups.toCollection().primaryKeys()
  return keys.filter((k): k is number => typeof k === 'number')
}

// ─── Helpers caché local de tipos de residuo ────────────────────────────────

export async function saveCachedWasteTypes(wasteTypes: WasteType[]): Promise<void> {
  // No sobreescribimos la caché con lista vacía para no perder datos offline si la API devuelve un resultado inesperado.
  if (wasteTypes.length === 0) return
  await db.waste_types.bulkPut(wasteTypes)
}

export async function getCachedWasteTypes(): Promise<WasteType[]> {
  return db.waste_types.toArray()
}

// ─── Helpers entregas a disposición final ────────────────────────────────────

export async function saveDeliveryDraft(
  draft: Omit<DeliveryDraft, 'id'>
): Promise<number> {
  return db.delivery_drafts.add(draft)
}

export async function getPendingDeliveryDrafts(): Promise<DeliveryDraft[]> {
  return db.delivery_drafts.where('pending_sync').equals(1).toArray()
}

export async function markDeliveryDraftSynced(id: number, serverId: number): Promise<void> {
  await db.delivery_drafts.update(id, {
    pending_sync: 0,
    server_id: serverId,
  })
}

export async function deleteDeliveryDraft(id: number): Promise<void> {
  await db.delivery_drafts.delete(id)
}
